import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';
import { showCreateSixModal, showEditSixModal } from '@/store/thunks/ui/uiThunk';
import { fetchSixCatalogThunk } from '@/store/thunks/sixCatalog/fetchSixCatalog';
import { setSearchAndFetchThunk } from '@/store/thunks/sixCatalog/setSearchAndFetchThunk';
import { setSortAndFetchThunk } from '@/store/thunks/sixCatalog/setSortAndFetchThunk';
import { deleteSixThunk } from '@/store/thunks/sixCatalog/deleteSixThunk';
import { toggleFavoriteSixThunk } from '@/store/thunks/sixCatalog/toggleFavoriteSixThunk';
import { setViewMode } from '@/store/slices/sixCatalog/sixCatalogSlice';
import {
  selectCatalogSixes,
  selectCatalogSearch,
  selectCatalogFilters,
  selectCatalogViewMode,
  selectHasPendingHapSix,
} from '@/store/selectors/sixCatalog';
import { useClientCatalogFiltering } from '@/hooks/sixCatalog/useClientCatalogFiltering';
import { selectConnectionStatusBySixId } from '@/store/selectors/connection';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { resolveIsOwner } from '@/utils/permissions';
import type { ConnectionStatus } from '@/store/slices/poc/connectionSlice';
import type { SixCatalogItem } from '@/types/Sixes';
import type { SortKey, SortOption, ViewMode } from '@/types/SixCatalog.types';
import { SORT_KEY_TO_PARAMS } from '@/types/SixCatalog.types';
import type { CatalogSortDirection } from '@/types/SixCatalogDTOs';

export type { SortKey, SortOption, ViewMode } from '@/types/SixCatalog.types';

export const SORT_OPTIONS: SortOption[] = [
  { key: 'lastActivity', label: 'Last Activity' },
  { key: 'name', label: 'Name' },
  { key: 'members', label: 'Members' },
  { key: 'files', label: 'Files' },
];

const SEARCH_DEBOUNCE_MS = 300;
const PENDING_HAP_POLL_MS = 15_000;

function toggleSortDirection(direction: CatalogSortDirection): CatalogSortDirection {
  return direction === 'asc' ? 'desc' : 'asc';
}

interface UseSixCatalogPageState {
  searchValue: string;
  selectedSortKey: SortKey;
  selectedSortDirection: CatalogSortDirection;
  viewMode: ViewMode;
  sortedSixes: SixCatalogItem[];
  showEmptyState: boolean;
  sortOptions: SortOption[];
  connectionStatusBySixId: Record<string, ConnectionStatus>;
  ownershipBySixId: Record<string, boolean>;
}

interface UseSixCatalogPageHandlers {
  handleSearchChange: (value: string) => void;
  handleSearchClear: () => void;
  handleSortChange: (nextValue: string) => void;
  handleSortColumnClick: (nextValue: string) => void;
  handleViewModeChange: (nextValue: string) => void;
  handleCardClick: (sixId: string) => void;
  handleCreateNewSix: () => void;
  handleToggleFavorite: (sixId: string) => void;
  handleEdit: (sixId: string) => void;
  handleDelete: (sixId: string) => void;
}

interface UseSixCatalogPageReturn {
  state: UseSixCatalogPageState;
  handlers: UseSixCatalogPageHandlers;
}

export function useSixCatalogPage(): UseSixCatalogPageReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sixes = useAppSelector(selectCatalogSixes);
  const hasPendingHapSix = useAppSelector(selectHasPendingHapSix);
  const storeSearch = useAppSelector(selectCatalogSearch);
  const activeFilters = useAppSelector(selectCatalogFilters);
  const viewMode = useAppSelector(selectCatalogViewMode);
  const connectionStatusBySixId = useAppSelector(selectConnectionStatusBySixId);
  const currentUserId = useAppSelector(selectCurrentUserId);

  const [searchValue, setSearchValue] = useState(storeSearch ?? '');
  const [selectedSortKey, setSelectedSortKey] = useState<SortKey>('lastActivity');
  const [selectedSortDirection, setSelectedSortDirection] = useState<CatalogSortDirection>(
    SORT_KEY_TO_PARAMS.lastActivity.sortDirection
  );
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch catalog on every mount so file counts stay current
  useEffect(() => {
    void dispatch(fetchSixCatalogThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!hasPendingHapSix) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void dispatch(fetchSixCatalogThunk());
    }, PENDING_HAP_POLL_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, hasPendingHapSix]);

  // Debounced search — dispatch API call after user stops typing
  useEffect(() => {
    const trimmedSearch = searchValue.trim();
    const currentStoreSearch = storeSearch ?? '';

    if (trimmedSearch === currentStoreSearch) return;

    debounceTimerRef.current = setTimeout(() => {
      void dispatch(setSearchAndFetchThunk(trimmedSearch.length > 0 ? trimmedSearch : undefined));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current !== undefined) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [dispatch, searchValue, storeSearch]);

  const sortedSixes = useClientCatalogFiltering({
    sixes,
    searchValue,
    activeFilters,
    currentUserId,
    selectedSortKey,
    selectedSortDirection,
  });

  const showEmptyState = sortedSixes.length === 0;

  const ownershipBySixId = useMemo<Record<string, boolean>>(() => {
    const result: Record<string, boolean> = {};
    for (const six of sixes) {
      result[String(six.id)] = resolveIsOwner(currentUserId, six.owners);
    }
    return result;
  }, [sixes, currentUserId]);

  const handleSearchChange = useCallback((value: string): void => {
    setSearchValue(value);
  }, []);

  const handleSearchClear = useCallback((): void => {
    setSearchValue('');
  }, []);

  const handleSortChange = useCallback(
    (nextValue: string): void => {
      const matchedOption = SORT_OPTIONS.find((option) => option.key === nextValue);
      if (matchedOption !== undefined) {
        const params = SORT_KEY_TO_PARAMS[matchedOption.key];
        setSelectedSortKey(matchedOption.key);
        setSelectedSortDirection(params.sortDirection);
        void dispatch(setSortAndFetchThunk(params));
      }
    },
    [dispatch]
  );

  const handleSortColumnClick = useCallback(
    (nextValue: string): void => {
      const matchedOption = SORT_OPTIONS.find((option) => option.key === nextValue);
      if (matchedOption === undefined) {
        return;
      }

      const params = SORT_KEY_TO_PARAMS[matchedOption.key];
      const isAlreadyActiveColumn = matchedOption.key === selectedSortKey;
      const nextDirection = isAlreadyActiveColumn
        ? toggleSortDirection(selectedSortDirection)
        : params.sortDirection;

      setSelectedSortKey(matchedOption.key);
      setSelectedSortDirection(nextDirection);
      void dispatch(
        setSortAndFetchThunk({ sortField: params.sortField, sortDirection: nextDirection })
      );
    },
    [dispatch, selectedSortKey, selectedSortDirection]
  );

  const handleViewModeChange = useCallback(
    (nextValue: string): void => {
      if (nextValue === 'grid' || nextValue === 'list') {
        dispatch(setViewMode(nextValue));
      }
    },
    [dispatch]
  );

  const handleCardClick = useCallback(
    (sixId: string): void => {
      void navigate({ to: `/app/sixes/${sixId}` });
    },
    [navigate]
  );

  const handleCreateNewSix = useCallback((): void => {
    dispatch(showCreateSixModal());
  }, [dispatch]);

  const handleToggleFavorite = useCallback(
    (sixId: string): void => {
      void dispatch(toggleFavoriteSixThunk(sixId))
        .unwrap()
        .catch(() => {
          showErrorToast('Failed to update favorite');
        });
    },
    [dispatch]
  );

  const handleEdit = useCallback(
    (sixId: string): void => {
      void dispatch(showEditSixModal(sixId));
    },
    [dispatch]
  );

  const handleDelete = useCallback(
    (sixId: string): void => {
      void dispatch(deleteSixThunk(sixId))
        .unwrap()
        .then(() => {
          showSuccessToast('SIX deleted');
        })
        .catch(() => {
          showErrorToast('Failed to delete SIX');
        });
    },
    [dispatch]
  );

  return {
    state: {
      searchValue,
      selectedSortKey,
      selectedSortDirection,
      viewMode,
      sortedSixes,
      showEmptyState,
      sortOptions: SORT_OPTIONS,
      connectionStatusBySixId,
      ownershipBySixId,
    },
    handlers: {
      handleSearchChange,
      handleSearchClear,
      handleSortChange,
      handleSortColumnClick,
      handleViewModeChange,
      handleCardClick,
      handleCreateNewSix,
      handleToggleFavorite,
      handleEdit,
      handleDelete,
    },
  };
}
